// ===============================================================
// ⭐ ChangePasswordPage.js — Change password (i18n + TopBar + LivoraToast)
// ===============================================================

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ChangePasswordPage.css";

import TopBar from "../components/common/TopBar";
import { useTranslation } from "react-i18next";
import useLivoraToast from "../hooks/useLivoraToast";
import LivoraToast from "../components/common/LivoraToast";

// Firebase
import { auth, db } from "../firebase";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";

function ChangePasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Livora Toast
  const { toast, showToast, hideToast } = useLivoraToast();

  const goBack = () => navigate(-1);

  // ===============================================================
  // 🔐 Change password
  // ===============================================================
  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!oldPass.trim() || !newPass.trim()) {
      showToast(
        "error",
        t("password_fill_required", {
          defaultValue: "يرجى تعبئة جميع الحقول",
        })
      );
      return;
    }

    if (newPass.length < 6) {
      showToast(
        "error",
        t("password_min_length", {
          defaultValue: "كلمة المرور الجديدة يجب أن تكون 6 أحرف أو أكثر",
        })
      );
      return;
    }

    try {
      setLoading(true);

      const user = auth.currentUser;
      if (!user) {
        showToast(
          "error",
          t("password_change_error", {
            defaultValue: "فشل تغيير كلمة المرور",
          })
        );
        return;
      }

      if (!user.email) {
        showToast(
          "error",
          t("email_not_available_for_reauth", {
            defaultValue:
              "لا يمكن إعادة التحقق لأن الحساب لا يحتوي على بريد إلكتروني",
          })
        );
        return;
      }

      // Re-authenticate
      const credential = EmailAuthProvider.credential(user.email, oldPass);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPass);

      // Optional Firestore update
      await updateDoc(doc(db, "users", user.uid), {
        lastSeen: serverTimestamp(),
      });

      showToast(
        "success",
        t("password_change_success", {
          defaultValue: "تم تغيير كلمة المرور بنجاح",
        })
      );

      setOldPass("");
      setNewPass("");

      setTimeout(() => {
        navigate("/settings");
      }, 500);
    } catch (error) {
      console.error(error);

      if (error.code === "auth/wrong-password") {
        showToast(
          "error",
          t("password_wrong_old", {
            defaultValue: "كلمة المرور القديمة غير صحيحة",
          })
        );
      } else if (error.code === "auth/weak-password") {
        showToast(
          "error",
          t("password_weak", {
            defaultValue: "كلمة المرور ضعيفة جدًا",
          })
        );
      } else if (error.code === "auth/requires-recent-login") {
        showToast(
          "error",
          t("requires_recent_login", {
            defaultValue: "يجب تسجيل الدخول مجددًا قبل تنفيذ هذا الإجراء",
          })
        );
      } else {
        showToast(
          "error",
          t("password_change_error", {
            defaultValue: "فشل تغيير كلمة المرور",
          })
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-page">
      <TopBar
        title={t("change_password", { defaultValue: "تغيير كلمة المرور" })}
        onBack={goBack}
      />

      <div className="page-content">
        <form onSubmit={handleChangePassword}>
          <input
            type="password"
            className="input-field"
            placeholder={t("old_password", {
              defaultValue: "كلمة المرور القديمة",
            })}
            value={oldPass}
            onChange={(e) => setOldPass(e.target.value)}
            autoComplete="current-password"
          />

          <input
            type="password"
            className="input-field"
            placeholder={t("new_password", {
              defaultValue: "كلمة المرور الجديدة",
            })}
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            autoComplete="new-password"
          />

          <button className="main-btn" type="submit" disabled={loading}>
            {loading
              ? t("changing_password", { defaultValue: "جارٍ التغيير..." })
              : t("change_password_button", {
                  defaultValue: "تغيير كلمة المرور",
                })}
          </button>
        </form>
      </div>

      {/* ✅ Livora Toast */}
      <LivoraToast toast={toast} onClose={hideToast} />
    </div>
  );
}

export default ChangePasswordPage;